<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="form-layout">
      <!-- 左側表單 -->
      <div class="form-container">
        <form @submit.prevent="handleSubmit">
          <!-- 基本設定 -->
          <div class="form-section">
            <h3>基本設定</h3>

            <div class="form-group">
              <label>平台名稱 <span class="required">*</span></label>
              <input
                v-model="form.platformName"
                type="text"
                class="form-control"
                required
                :placeholder="platformNamePlaceholder"
              />
              <small class="form-text">用於識別此設定的名稱，可自訂</small>
            </div>

            <div class="form-group">
              <label>連線方式 <span class="required">*</span></label>
              <select v-model="form.connectionType" class="form-control" required>
                <option value="webhook">Webhook（推薦）</option>
                <option value="api_polling">API 輪詢</option>
              </select>
              <small class="form-text">{{ connectionTypeHint }}</small>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input v-model="form.isActive" type="checkbox" />
                啟用此平台
              </label>
            </div>
          </div>

          <!-- Webhook 設定 -->
          <div v-if="form.connectionType === 'webhook'" class="form-section">
            <h3>Webhook 設定</h3>

            <div class="form-group">
              <label>Webhook URL（複製此網址到 {{ getPlatformDisplayName(currentPlatform) }}）</label>
              <div class="input-copy-group">
                <input
                  :value="form.webhookUrl"
                  type="text"
                  class="form-control"
                  readonly
                />
                <button type="button" class="btn btn-outline btn-sm" @click="copyWebhookUrl">
                  <i :class="copied ? 'ri-check-line' : 'ri-clipboard-line'"></i>
                  {{ copied ? '已複製' : '複製' }}
                </button>
              </div>
              <small class="form-text">{{ webhookUrlHint }}</small>
            </div>

            <div class="form-group">
              <label>Webhook Secret <span class="required">*</span></label>
              <div class="input-generate-group">
                <input
                  v-model="form.webhookSecret"
                  :type="showSecret ? 'text' : 'password'"
                  class="form-control"
                  required
                  placeholder="請輸入或產生一組 Secret"
                />
                <button type="button" class="btn btn-outline btn-sm" @click="showSecret = !showSecret">
                  <i :class="showSecret ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
                <button type="button" class="btn btn-outline btn-sm" @click="generateSecret">
                  <i class="ri-refresh-line"></i>
                  產生
                </button>
              </div>
              <small class="form-text">{{ webhookSecretHint }}</small>
            </div>

            <div class="form-group">
              <label>訂閱事件</label>
              <div v-if="currentPlatform === 'woocommerce'" class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.created" />
                  訂單建立（<code>order.created</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.updated" />
                  訂單更新（<code>order.updated</code>）— 含狀態變更、取消、完成等
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.deleted" />
                  訂單刪除（<code>order.deleted</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.restored" />
                  訂單還原（<code>order.restored</code>）
                </label>
                <small class="form-text">
                  WooCommerce 沒有獨立的「訂單取消」主題，取消事件會透過 <code>order.updated</code> 觸發，payload 中 status 為 <code>cancelled</code>
                </small>
              </div>
              <div v-else-if="currentPlatform === 'shopify'" class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/create" />
                  訂單建立（<code>orders/create</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/updated" />
                  訂單更新（<code>orders/updated</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/paid" />
                  訂單付款（<code>orders/paid</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/cancelled" />
                  訂單取消（<code>orders/cancelled</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/fulfilled" />
                  訂單出貨（<code>orders/fulfilled</code>）
                </label>
                <small class="form-text">
                  Shopify 每個 Webhook 主題需在後台分別建立；建議最少勾選 <code>orders/create</code> 和 <code>orders/updated</code>
                </small>
              </div>
              <div v-else-if="currentPlatform === 'shopline'" class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order_created" />
                  訂單建立（<code>order_created</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order_updated" />
                  訂單更新（<code>order_updated</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order_paid" />
                  訂單付款（<code>order_paid</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order_cancelled" />
                  訂單取消（<code>order_cancelled</code>）
                </label>
                <small class="form-text">
                  Shopline Webhook 支援透過 Open API 管理；建議最少勾選 <code>order_created</code> 和 <code>order_updated</code>
                </small>
              </div>
              <div v-else-if="currentPlatform === '91app'" class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.created" />
                  訂單建立（<code>order.created</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.status_changed" />
                  訂單狀態變更（<code>order.status_changed</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.cancelled" />
                  訂單取消（<code>order.cancelled</code>）
                </label>
                <small class="form-text">
                  91APP Webhook 需聯繫 91APP 客服申請，並提供本系統的 Webhook URL 與 Secret。請至少設定 <code>order.created</code> 和 <code>order.status_changed</code>
                </small>
              </div>
              <div v-else-if="currentPlatform === 'cyberbiz'" class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/create" />
                  訂單建立（<code>orders/create</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/paid" />
                  訂單付款（<code>orders/paid</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/update" />
                  訂單更新（<code>orders/update</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="orders/cancel" />
                  訂單取消（<code>orders/cancel</code>）
                </label>
                <small class="form-text">
                  至 CYBERBIZ 後台 → 進階應用 → Webhook → 新增，填入本系統 Webhook URL 並勾選對應事件。建議最少勾選 <code>orders/create</code> 和 <code>orders/paid</code>
                </small>
              </div>
              <div v-else-if="currentPlatform === 'shopee'" class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="ORDER_STATUS_UPDATE" />
                  訂單狀態變更（<code>ORDER_STATUS_UPDATE</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="ORDER_CANCEL_REASON_UPDATED" />
                  訂單取消原因更新（<code>ORDER_CANCEL_REASON_UPDATED</code>）
                </label>
                <small class="form-text">
                  蝦皮 Webhook 需在 Open Platform 後台設定，所有訂單狀態變更（建立、付款、出貨、完成、取消）均透過 <code>ORDER_STATUS_UPDATE</code> 推送。
                </small>
              </div>
              <div v-else-if="currentPlatform === 'meepshop'" class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="ORDER_CREATED" />
                  訂單建立（<code>ORDER_CREATED</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="ORDER_PAID" />
                  訂單付款（<code>ORDER_PAID</code>）
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="ORDER_STATUS_UPDATED" />
                  訂單狀態更新（<code>ORDER_STATUS_UPDATED</code>）
                </label>
                <small class="form-text">
                  meepShop Webhook 需透過後台 → 設定 → 開放 API → Webhook 設定。建議至少訂閱 <code>ORDER_CREATED</code> 和 <code>ORDER_PAID</code>。
                </small>
              </div>
              <div v-else class="checkbox-group">
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.created" />
                  訂單建立
                </label>
                <label class="checkbox-label">
                  <input v-model="form.webhookEvents" type="checkbox" value="order.updated" />
                  訂單更新
                </label>
              </div>
            </div>
          </div>

          <!-- API 輪詢設定 -->
          <div v-if="form.connectionType === 'api_polling'" class="form-section">
            <h3>API 設定</h3>

            <div class="form-group">
              <label>{{ apiUrlLabel }} <span class="required">*</span></label>
              <input
                v-model="form.apiUrl"
                type="url"
                class="form-control"
                required
                :placeholder="apiUrlPlaceholder"
              />
              <small class="form-text">{{ apiUrlHint }}</small>
            </div>

            <div class="form-group">
              <label>{{ apiKeyLabel }} <span class="required">*</span></label>
              <input
                v-model="form.apiKey"
                type="text"
                class="form-control"
                required
                :placeholder="apiKeyPlaceholder"
              />
              <small class="form-text">{{ apiKeyHint }}</small>
            </div>

            <div v-if="showApiSecretField" class="form-group">
              <label>{{ apiSecretLabel }} <span class="required">*</span></label>
              <input
                v-model="form.apiSecret"
                :type="showSecret ? 'text' : 'password'"
                class="form-control"
                required
                :placeholder="apiSecretPlaceholder"
              />
              <div class="input-actions">
                <button type="button" class="btn-text" @click="showSecret = !showSecret">
                  <i :class="showSecret ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  {{ showSecret ? '隱藏' : '顯示' }}
                </button>
              </div>
            </div>

            <div class="form-group">
              <label>輪詢間隔（分鐘）</label>
              <input
                v-model.number="form.pollInterval"
                type="number"
                class="form-control"
                min="5"
                placeholder="預設：15"
              />
              <small class="form-text">建議不低於 5 分鐘，或使用手動觸發</small>
            </div>

            <!-- 儲存後顯示手動輪詢按鈕 -->
            <div v-if="isEdit" class="manual-poll-section">
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="polling"
                @click="handleManualPoll"
              >
                <i class="ri-refresh-line" :class="{ 'spin': polling }"></i>
                {{ polling ? '輪詢中...' : '立即執行輪詢' }}
              </button>
              <div v-if="pollResult" class="poll-result" :class="pollResult.success ? 'success' : 'error'">
                <span>拉取 {{ pollResult.ordersTotal }} 筆，成功 {{ pollResult.ordersSuccess }} 筆，失敗 {{ pollResult.ordersFailed }} 筆</span>
              </div>
            </div>
          </div>

          <!-- 欄位映射 -->
          <div class="form-section">
            <div class="section-header-toggle" @click="showMappingEditor = !showMappingEditor">
              <h3>
                欄位映射設定
                <span class="badge badge-secondary">進階</span>
              </h3>
              <i :class="showMappingEditor ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
            </div>

            <div v-if="showMappingEditor" class="mapping-editor">

              <!-- WooCommerce 視覺化對照表 -->
              <div v-if="currentPlatform === 'woocommerce'">
                <div class="mapping-notice">
                  <i class="ri-checkbox-circle-fill"></i>
                  WooCommerce 欄位對照已自動設定完成，<strong>一般不需要修改</strong>。
                  若您的 WooCommerce 有特殊外掛導致欄位路徑不同，才需要調整。
                </div>

                <p class="mapping-subtitle">欄位對照說明（WooCommerce 訂單 → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>本系統欄位</div>
                    <div>說明</div>
                    <div>從 WooCommerce 取得路徑</div>
                    <div>範例值</div>
                  </div>
                  <div v-for="row in wcFieldMappingRows" :key="row.key" class="mvt-row">
                    <div class="mvt-key"><code>{{ row.key }}</code></div>
                    <div class="mvt-desc">{{ row.desc }}</div>
                    <div class="mvt-path"><code>{{ row.path }}</code></div>
                    <div class="mvt-example">{{ row.example }}</div>
                  </div>
                </div>

                <p class="mapping-subtitle" style="margin-top: 20px;">訂單狀態對照（WooCommerce → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>WooCommerce 狀態</div>
                    <div>說明</div>
                    <div>對應系統狀態</div>
                    <div></div>
                  </div>
                  <div v-for="row in wcStatusMappingRows" :key="row.wc" class="mvt-row">
                    <div><code>{{ row.wc }}</code></div>
                    <div>{{ row.wcDesc }}</div>
                    <div><code>{{ row.system }}</code></div>
                    <div>{{ row.systemDesc }}</div>
                  </div>
                </div>

                <!-- 進階：展開 JSON 編輯 -->
                <div class="advanced-json-toggle" @click="showRawJson = !showRawJson">
                  <i class="ri-code-line"></i>
                  {{ showRawJson ? '收起 JSON 編輯' : '進階：直接編輯 JSON' }}
                </div>
              </div>

              <!-- Shopify 視覺化對照表 -->
              <div v-if="currentPlatform === 'shopify'">
                <div class="mapping-notice">
                  <i class="ri-checkbox-circle-fill"></i>
                  Shopify 欄位對照已自動設定完成，<strong>一般不需要修改</strong>。
                  本系統使用專屬的 Shopify 訂單解析邏輯，設定僅供參考。
                </div>

                <p class="mapping-subtitle">欄位對照說明（Shopify 訂單 → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>本系統欄位</div>
                    <div>說明</div>
                    <div>從 Shopify 取得路徑</div>
                    <div>範例值</div>
                  </div>
                  <div v-for="row in shopifyFieldMappingRows" :key="row.key" class="mvt-row">
                    <div class="mvt-key"><code>{{ row.key }}</code></div>
                    <div class="mvt-desc">{{ row.desc }}</div>
                    <div class="mvt-path"><code>{{ row.path }}</code></div>
                    <div class="mvt-example">{{ row.example }}</div>
                  </div>
                </div>

                <p class="mapping-subtitle" style="margin-top: 20px;">訂單狀態對照（Shopify financial_status → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>Shopify 狀態</div>
                    <div>說明</div>
                    <div>對應系統狀態</div>
                    <div></div>
                  </div>
                  <div v-for="row in shopifyStatusMappingRows" :key="row.wc" class="mvt-row">
                    <div><code>{{ row.wc }}</code></div>
                    <div>{{ row.wcDesc }}</div>
                    <div><code>{{ row.system }}</code></div>
                    <div>{{ row.systemDesc }}</div>
                  </div>
                </div>

                <div class="advanced-json-toggle" @click="showRawJson = !showRawJson">
                  <i class="ri-code-line"></i>
                  {{ showRawJson ? '收起 JSON 編輯' : '進階：直接編輯 JSON' }}
                </div>
              </div>

              <!-- Shopline 視覺化對照表 -->
              <div v-if="currentPlatform === 'shopline'">
                <div class="mapping-notice">
                  <i class="ri-checkbox-circle-fill"></i>
                  Shopline 欄位對照已自動設定完成，<strong>一般不需要修改</strong>。
                  本系統使用專屬的 Shopline 訂單解析邏輯，設定僅供參考。
                </div>

                <p class="mapping-subtitle">欄位對照說明（Shopline 訂單 → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>本系統欄位</div>
                    <div>說明</div>
                    <div>從 Shopline 取得路徑</div>
                    <div>範例值</div>
                  </div>
                  <div v-for="row in shoplineFieldMappingRows" :key="row.key" class="mvt-row">
                    <div class="mvt-key"><code>{{ row.key }}</code></div>
                    <div class="mvt-desc">{{ row.desc }}</div>
                    <div class="mvt-path"><code>{{ row.path }}</code></div>
                    <div class="mvt-example">{{ row.example }}</div>
                  </div>
                </div>

                <p class="mapping-subtitle" style="margin-top: 20px;">訂單狀態對照（Shopline → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>Shopline 狀態</div>
                    <div>說明</div>
                    <div>對應系統狀態</div>
                    <div></div>
                  </div>
                  <div v-for="row in shoplineStatusMappingRows" :key="row.wc" class="mvt-row">
                    <div><code>{{ row.wc }}</code></div>
                    <div>{{ row.wcDesc }}</div>
                    <div><code>{{ row.system }}</code></div>
                    <div>{{ row.systemDesc }}</div>
                  </div>
                </div>

                <div class="advanced-json-toggle" @click="showRawJson = !showRawJson">
                  <i class="ri-code-line"></i>
                  {{ showRawJson ? '收起 JSON 編輯' : '進階：直接編輯 JSON' }}
                </div>
              </div>

              <!-- 91APP 視覺化對照表 -->
              <div v-if="currentPlatform === '91app'">
                <div class="mapping-notice">
                  <i class="ri-checkbox-circle-fill"></i>
                  91APP 欄位對照已自動設定完成，<strong>一般不需要修改</strong>。
                  本系統使用專屬的 91APP 訂單解析邏輯，設定僅供參考。
                </div>

                <p class="mapping-subtitle">欄位對照說明（91APP TS 子單 → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>本系統欄位</div>
                    <div>說明</div>
                    <div>從 91APP 取得路徑</div>
                    <div>範例值</div>
                  </div>
                  <div v-for="row in app91FieldMappingRows" :key="row.key" class="mvt-row">
                    <div class="mvt-key"><code>{{ row.key }}</code></div>
                    <div class="mvt-desc">{{ row.desc }}</div>
                    <div class="mvt-path"><code>{{ row.path }}</code></div>
                    <div class="mvt-example">{{ row.example }}</div>
                  </div>
                </div>

                <p class="mapping-subtitle" style="margin-top: 20px;">訂單狀態對照（91APP → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>91APP 狀態</div>
                    <div>說明</div>
                    <div>對應系統狀態</div>
                    <div></div>
                  </div>
                  <div v-for="row in app91StatusMappingRows" :key="row.wc" class="mvt-row">
                    <div><code>{{ row.wc }}</code></div>
                    <div>{{ row.wcDesc }}</div>
                    <div><code>{{ row.system }}</code></div>
                    <div>{{ row.systemDesc }}</div>
                  </div>
                </div>

                <div class="advanced-json-toggle" @click="showRawJson = !showRawJson">
                  <i class="ri-code-line"></i>
                  {{ showRawJson ? '收起 JSON 編輯' : '進階：直接編輯 JSON' }}
                </div>
              </div>

              <!-- CYBERBIZ 視覺化對照表 -->
              <div v-if="currentPlatform === 'cyberbiz'">
                <div class="mapping-notice">
                  <i class="ri-checkbox-circle-fill"></i>
                  CYBERBIZ 欄位對照已自動設定完成，<strong>一般不需要修改</strong>。
                  本系統使用專屬的 CYBERBIZ 訂單解析邏輯，設定僅供參考。
                </div>

                <p class="mapping-subtitle">欄位對照說明（CYBERBIZ 訂單 → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>本系統欄位</div>
                    <div>說明</div>
                    <div>從 CYBERBIZ 取得路徑</div>
                    <div>範例值</div>
                  </div>
                  <div v-for="row in cyberbizFieldMappingRows" :key="row.key" class="mvt-row">
                    <div class="mvt-key"><code>{{ row.key }}</code></div>
                    <div class="mvt-desc">{{ row.desc }}</div>
                    <div class="mvt-path"><code>{{ row.path }}</code></div>
                    <div class="mvt-example">{{ row.example }}</div>
                  </div>
                </div>

                <p class="mapping-subtitle" style="margin-top: 20px;">訂單狀態對照（CYBERBIZ financial_status → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>CYBERBIZ 狀態</div>
                    <div>說明</div>
                    <div>對應系統狀態</div>
                    <div></div>
                  </div>
                  <div v-for="row in cyberbizStatusMappingRows" :key="row.wc" class="mvt-row">
                    <div><code>{{ row.wc }}</code></div>
                    <div>{{ row.wcDesc }}</div>
                    <div><code>{{ row.system }}</code></div>
                    <div>{{ row.systemDesc }}</div>
                  </div>
                </div>

                <div class="advanced-json-toggle" @click="showRawJson = !showRawJson">
                  <i class="ri-code-line"></i>
                  {{ showRawJson ? '收起 JSON 編輯' : '進階：直接編輯 JSON' }}
                </div>
              </div>

              <!-- Shopee 視覺化對照表 -->
              <div v-if="currentPlatform === 'shopee'">
                <div class="mapping-notice">
                  <i class="ri-checkbox-circle-fill"></i>
                  蝦皮 Shopee 欄位對照已自動設定完成，<strong>一般不需要修改</strong>。
                  本系統使用專屬的 Shopee 訂單解析邏輯，設定僅供參考。
                </div>

                <p class="mapping-subtitle">欄位對照說明（Shopee 訂單 → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>本系統欄位</div>
                    <div>說明</div>
                    <div>從 Shopee 取得路徑</div>
                    <div>範例值</div>
                  </div>
                  <div v-for="row in shopeeFieldMappingRows" :key="row.key" class="mvt-row">
                    <div class="mvt-key"><code>{{ row.key }}</code></div>
                    <div class="mvt-desc">{{ row.desc }}</div>
                    <div class="mvt-path"><code>{{ row.path }}</code></div>
                    <div class="mvt-example">{{ row.example }}</div>
                  </div>
                </div>

                <p class="mapping-subtitle" style="margin-top: 20px;">訂單狀態對照（Shopee order_status → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>Shopee 狀態</div>
                    <div>說明</div>
                    <div>對應系統狀態</div>
                    <div></div>
                  </div>
                  <div v-for="row in shopeeStatusMappingRows" :key="row.wc" class="mvt-row">
                    <div><code>{{ row.wc }}</code></div>
                    <div>{{ row.wcDesc }}</div>
                    <div><code>{{ row.system }}</code></div>
                    <div>{{ row.systemDesc }}</div>
                  </div>
                </div>

                <div class="advanced-json-toggle" @click="showRawJson = !showRawJson">
                  <i class="ri-code-line"></i>
                  {{ showRawJson ? '收起 JSON 編輯' : '進階：直接編輯 JSON' }}
                </div>
              </div>

              <!-- meepShop 視覺化對照表 -->
              <div v-if="currentPlatform === 'meepshop'">
                <div class="mapping-notice">
                  <i class="ri-checkbox-circle-fill"></i>
                  meepShop 欄位對照已自動設定完成，<strong>一般不需要修改</strong>。
                  本系統使用專屬的 meepShop 訂單解析邏輯，設定僅供參考。
                </div>

                <p class="mapping-subtitle">欄位對照說明（meepShop 訂單 → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>本系統欄位</div>
                    <div>說明</div>
                    <div>從 meepShop 取得路徑</div>
                    <div>範例值</div>
                  </div>
                  <div v-for="row in meepshopFieldMappingRows" :key="row.key" class="mvt-row">
                    <div class="mvt-key"><code>{{ row.key }}</code></div>
                    <div class="mvt-desc">{{ row.desc }}</div>
                    <div class="mvt-path"><code>{{ row.path }}</code></div>
                    <div class="mvt-example">{{ row.example }}</div>
                  </div>
                </div>

                <p class="mapping-subtitle" style="margin-top: 20px;">訂單狀態對照（meepShop → 本系統）</p>

                <div class="mapping-visual-table">
                  <div class="mvt-header">
                    <div>meepShop 狀態</div>
                    <div>說明</div>
                    <div>對應系統狀態</div>
                    <div></div>
                  </div>
                  <div v-for="row in meepshopStatusMappingRows" :key="row.wc" class="mvt-row">
                    <div><code>{{ row.wc }}</code></div>
                    <div>{{ row.wcDesc }}</div>
                    <div><code>{{ row.system }}</code></div>
                    <div>{{ row.systemDesc }}</div>
                  </div>
                </div>

                <div class="advanced-json-toggle" @click="showRawJson = !showRawJson">
                  <i class="ri-code-line"></i>
                  {{ showRawJson ? '收起 JSON 編輯' : '進階：直接編輯 JSON' }}
                </div>
              </div>

              <!-- 其他平台（無視覺化）或展開 JSON -->
              <div v-if="!['woocommerce', 'shopify', 'shopline', '91app', 'cyberbiz', 'shopee', 'meepshop'].includes(currentPlatform) || showRawJson">
                <div class="form-group">
                  <label>欄位映射（JSON 格式）</label>
                  <textarea
                    v-model="fieldMappingJson"
                    class="form-control json-textarea"
                    rows="10"
                    placeholder="{}"
                    @blur="validateFieldMappingJson"
                  ></textarea>
                  <small v-if="fieldMappingError" class="form-text error-text">{{ fieldMappingError }}</small>
                  <small v-else class="form-text">
                    格式：<code>{ "系統欄位名": "平台欄位路徑" }</code>，用點號（.）表示巢狀，例如 <code>"billing.email"</code>
                  </small>
                </div>

                <div class="form-group">
                  <label>訂單狀態映射（JSON 格式）</label>
                  <textarea
                    v-model="statusMappingJson"
                    class="form-control json-textarea"
                    rows="8"
                    placeholder="{}"
                    @blur="validateStatusMappingJson"
                  ></textarea>
                  <small v-if="statusMappingError" class="form-text error-text">{{ statusMappingError }}</small>
                  <small v-else class="form-text">
                    格式：<code>{ "平台狀態": "系統狀態" }</code>，系統狀態可為 pending_payment / processing（準備出貨）/ shipped（已出貨）/ completed（已下單）/ cancelled / refunded
                  </small>
                </div>
              </div>

            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" @click="handleBack">取消</button>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              {{ submitting ? '儲存中...' : '儲存設定' }}
            </button>
          </div>
        </form>
      </div>

      <!-- 右側設定說明 -->
      <div class="guide-panel">
        <!-- WooCommerce Webhook 設定說明 -->
        <div v-if="currentPlatform === 'woocommerce' && form.connectionType === 'webhook'" class="guide-card">
          <h4><i class="ri-guide-line"></i> WooCommerce Webhook 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>登入 WordPress 後台</strong>
              <p>前往 WooCommerce → 設定 → 進階 → Webhooks</p>
            </li>
            <li>
              <strong>新增 Webhook</strong>
              <p>點選「新增 Webhook」按鈕</p>
            </li>
            <li>
              <strong>填入設定</strong>
              <ul>
                <li>名稱：自訂（例如：CRM 訂單同步）</li>
                <li>狀態：<strong>啟用</strong></li>
                <li>主題（Topic）：<strong>Order created</strong>（建議再新增一個 Order updated）</li>
                <li>傳送網址：<code class="code-inline">{{ form.webhookUrl }}</code></li>
                <li>Secret：填入上方「Webhook Secret」的值</li>
                <li>API 版本：WP REST API Integration v3</li>
              </ul>
              <div style="margin-top: 6px; font-size: 12px; color: #888;">
                每個 WooCommerce Webhook 只能訂閱一個 Topic，需分別建立多筆 Webhook 才能訂閱多個事件。
              </div>
            </li>
            <li>
              <strong>儲存 Webhook</strong>
              <p>點選「儲存 Webhook」完成設定</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            建議同時加入「Order updated」主題，以便追蹤訂單狀態變更。
          </div>
        </div>

        <!-- WooCommerce API 輪詢設定說明 -->
        <div v-if="currentPlatform === 'woocommerce' && form.connectionType === 'api_polling'" class="guide-card">
          <h4><i class="ri-guide-line"></i> WooCommerce REST API 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>登入 WordPress 後台</strong>
              <p>前往 WooCommerce → 設定 → 進階 → REST API</p>
            </li>
            <li>
              <strong>新增 API 金鑰</strong>
              <p>點選「新增金鑰」按鈕</p>
            </li>
            <li>
              <strong>填入設定</strong>
              <ul>
                <li>說明：自訂（例如：CRM 整合）</li>
                <li>使用者：選擇管理員帳號</li>
                <li>權限：<strong>讀取</strong>（Read）</li>
              </ul>
            </li>
            <li>
              <strong>複製金鑰</strong>
              <p>產生後複製 <strong>Consumer Key</strong> 和 <strong>Consumer Secret</strong>（只顯示一次）</p>
            </li>
            <li>
              <strong>填入本頁表單</strong>
              <p>將金鑰填入左側 Consumer Key 和 Consumer Secret 欄位</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            輪詢模式為主動拉取，建議搭配「立即執行輪詢」按鈕測試連線是否正常。
          </div>
        </div>

        <!-- WooCommerce 狀態對照 -->
        <div v-if="currentPlatform === 'woocommerce'" class="guide-card">
          <h4><i class="ri-exchange-line"></i> WooCommerce 訂單狀態對照</h4>
          <div class="status-table-wrapper">
            <table class="status-table">
              <thead>
                <tr>
                  <th>WooCommerce 狀態</th>
                  <th>系統狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(target, source) in wooStatusMapping" :key="source">
                  <td><code>{{ source }}</code></td>
                  <td>{{ statusLabels[target] || target }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Shopify Webhook 設定說明 -->
        <div v-if="currentPlatform === 'shopify' && form.connectionType === 'webhook'" class="guide-card">
          <h4><i class="ri-guide-line"></i> Shopify Webhook 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>登入 Shopify 後台</strong>
              <p>前往 Settings（設定）→ Notifications（通知）→ 頁面最下方 Webhooks</p>
            </li>
            <li>
              <strong>新增 Webhook</strong>
              <p>點選「Create webhook」按鈕</p>
            </li>
            <li>
              <strong>填入設定（每個事件需分別建立）</strong>
              <ul>
                <li>Event：選擇 <strong>Order creation</strong></li>
                <li>Format：JSON</li>
                <li>URL：<code class="code-inline">{{ form.webhookUrl }}</code></li>
              </ul>
              <div style="margin-top: 6px; font-size: 12px; color: #888;">
                重複步驟建立「Order update」Webhook，用於追蹤狀態變更。
              </div>
            </li>
            <li>
              <strong>複製 Signing Secret</strong>
              <p>建立後 Shopify 會顯示一組 Signing Secret，請將此值填入左側「Webhook Secret」欄位</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            Shopify Webhook 簽章使用 HMAC-SHA256（Base64 編碼），本系統已支援自動驗證。
          </div>
        </div>

        <!-- Shopify API 輪詢設定說明 -->
        <div v-if="currentPlatform === 'shopify' && form.connectionType === 'api_polling'" class="guide-card">
          <h4><i class="ri-guide-line"></i> Shopify Admin API 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>登入 Shopify 後台</strong>
              <p>前往 Settings（設定）→ Apps and sales channels → 選擇 Develop apps</p>
            </li>
            <li>
              <strong>建立 Custom App</strong>
              <p>點選「Create an app」，填入應用名稱（例如：CRM 整合）</p>
            </li>
            <li>
              <strong>設定 API 權限</strong>
              <ul>
                <li>前往 Configuration → Admin API integration</li>
                <li>勾選 <strong>read_orders</strong> 權限</li>
                <li>點選 Save</li>
              </ul>
            </li>
            <li>
              <strong>取得 Access Token</strong>
              <p>前往 API credentials → 點選「Install app」→ 複製 <strong>Admin API access token</strong>（只顯示一次）</p>
            </li>
            <li>
              <strong>填入本頁表單</strong>
              <ul>
                <li>Shop URL：填入您的 Shopify 網址（例如：<code>your-store.myshopify.com</code>）</li>
                <li>Access Token：填入上方複製的 token</li>
              </ul>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            Shopify Access Token 只會顯示一次，請妥善保存。若遺失需重新產生。
          </div>
        </div>

        <!-- Shopify 狀態對照 -->
        <div v-if="currentPlatform === 'shopify'" class="guide-card">
          <h4><i class="ri-exchange-line"></i> Shopify 訂單狀態對照</h4>
          <div class="status-table-wrapper">
            <table class="status-table">
              <thead>
                <tr>
                  <th>Shopify 狀態（financial_status）</th>
                  <th>系統狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(target, source) in shopifyStatusMapping" :key="source">
                  <td><code>{{ source }}</code></td>
                  <td>{{ statusLabels[target] || target }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Shopline Webhook 設定說明 -->
        <div v-if="currentPlatform === 'shopline' && form.connectionType === 'webhook'" class="guide-card">
          <h4><i class="ri-guide-line"></i> Shopline Webhook 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>取得 Shopline Open API 憑證</strong>
              <p>登入 Shopline 後台 → 應用程式 → Open API → 複製 Access Token</p>
            </li>
            <li>
              <strong>透過 API 建立 Webhook</strong>
              <p>呼叫 Shopline API 建立 Webhook 訂閱（需要 Access Token）：</p>
              <div style="background: #f0f0f0; padding: 10px; border-radius: 4px; font-size: 12px; margin-top: 6px; word-break: break-all;">
                POST https://open.shopline.io/v1/webhooks<br/>
                Authorization: Bearer &lt;Access Token&gt;
              </div>
            </li>
            <li>
              <strong>設定事件與目標 URL</strong>
              <ul>
                <li>event：<code>order_created</code>（訂單建立）</li>
                <li>address：填入上方的 Webhook URL</li>
              </ul>
            </li>
            <li>
              <strong>複製 Webhook Secret</strong>
              <p>建立成功後，將 Shopline 回傳的 secret 填入左側「Webhook Secret」欄位</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            Shopline Open API 速率限制：20 req/s，若大量訂單可能需要批次處理。
          </div>
        </div>

        <!-- Shopline API 輪詢設定說明 -->
        <div v-if="currentPlatform === 'shopline' && form.connectionType === 'api_polling'" class="guide-card">
          <h4><i class="ri-guide-line"></i> Shopline Open API 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>申請 Shopline Open API</strong>
              <p>登入 Shopline 後台 → 應用程式 → Open API 申請存取</p>
            </li>
            <li>
              <strong>取得 Access Token</strong>
              <p>申請通過後，在 Shopline 後台複製 <strong>Access Token</strong></p>
            </li>
            <li>
              <strong>填入本頁表單</strong>
              <ul>
                <li>Shop Handle：填入您的 Shopline 商店代碼（handle）</li>
                <li>Access Token：填入上方複製的 token</li>
              </ul>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            Shopline Open API 速率限制：20 req/s。Access Token 有效期 3 年（Private App）。
          </div>
        </div>

        <!-- Shopline 狀態對照 -->
        <div v-if="currentPlatform === 'shopline'" class="guide-card">
          <h4><i class="ri-exchange-line"></i> Shopline 訂單狀態對照</h4>
          <div class="status-table-wrapper">
            <table class="status-table">
              <thead>
                <tr>
                  <th>Shopline 狀態</th>
                  <th>系統狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(target, source) in shoplineStatusMapping" :key="source">
                  <td><code>{{ source }}</code></td>
                  <td>{{ statusLabels[target] || target }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 91APP Webhook 設定說明 -->
        <div v-if="currentPlatform === '91app' && form.connectionType === 'webhook'" class="guide-card">
          <h4><i class="ri-guide-line"></i> 91APP Webhook 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>申請 91APP API Key</strong>
              <p>登入 91APP OMNI → 公司資訊與方案 → 服務申請與資料異動，填寫申請表，取得 <code>x-api-key</code></p>
            </li>
            <li>
              <strong>聯繫 91APP 客服申請 Webhook</strong>
              <p>91APP Webhook 需主動聯繫 91APP 技術支援（<a href="mailto:hd@91apptw.zohodesk.com">hd@91apptw.zohodesk.com</a>），提供以下資訊：</p>
              <ul>
                <li>Webhook URL：<code>{{ form.webhookUrl }}</code></li>
                <li>需訂閱的事件類型</li>
              </ul>
            </li>
            <li>
              <strong>填入 Webhook Secret</strong>
              <p>91APP 確認後會提供 Shared Secret，請將其填入左側「Webhook Secret」欄位</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            91APP Admin API 速率限制：依方案不同，請參考官方文件。Webhook 需透過 91APP 服務窗口開通，無法自助設定。
          </div>
        </div>

        <!-- 91APP API 輪詢設定說明 -->
        <div v-if="currentPlatform === '91app' && form.connectionType === 'api_polling'" class="guide-card">
          <h4><i class="ri-guide-line"></i> 91APP Admin API 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>申請 91APP API Key</strong>
              <p>登入 91APP OMNI → 公司資訊與方案 → 服務申請與資料異動，申請後取得 <code>x-api-key</code></p>
            </li>
            <li>
              <strong>取得 ShopId</strong>
              <p>登入 91APP OMNI 後台，可在商店設定或 API 文件中找到您的數字型 ShopId</p>
            </li>
            <li>
              <strong>填入本頁表單</strong>
              <ul>
                <li>ShopId：填入數字型的商店 ID，例如 <code>12345</code></li>
                <li>x-api-key：填入申請取得的 API Key</li>
              </ul>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            91APP 採用 TS 子單為最小訂單單位（每個商品品項對應一個 TS）；系統會以 TS 編號為唯一識別進行同步。
          </div>
        </div>

        <!-- 91APP 狀態對照 -->
        <div v-if="currentPlatform === '91app'" class="guide-card">
          <h4><i class="ri-exchange-line"></i> 91APP 訂單狀態對照</h4>
          <div class="status-table-wrapper">
            <table class="status-table">
              <thead>
                <tr>
                  <th>91APP 狀態</th>
                  <th>系統狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(target, source) in app91StatusMapping" :key="source">
                  <td><code>{{ source }}</code></td>
                  <td>{{ statusLabels[target] || target }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- CYBERBIZ Webhook 設定說明 -->
        <div v-if="currentPlatform === 'cyberbiz' && form.connectionType === 'webhook'" class="guide-card">
          <h4><i class="ri-guide-line"></i> CYBERBIZ Webhook 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>登入 CYBERBIZ 後台</strong>
              <p>前往 進階應用 → Webhook</p>
            </li>
            <li>
              <strong>新增 Webhook</strong>
              <p>點選「新增 Webhook」按鈕</p>
            </li>
            <li>
              <strong>填入設定</strong>
              <ul>
                <li>Webhook URL：貼入上方複製的 URL</li>
                <li>訂閱事件：勾選「訂單建立（orders/create）」、「訂單付款（orders/paid）」等</li>
              </ul>
            </li>
            <li>
              <strong>複製 HMAC Key</strong>
              <p>設定儲存後，CYBERBIZ 會產生一組 HMAC Key，請將此值填入左側「Webhook Secret」欄位</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            CYBERBIZ Webhook 功能需為企業版（免費）或 PLUS 方案（加購）。速率限制：每秒最多 5 個請求。
          </div>
        </div>

        <!-- CYBERBIZ API 輪詢設定說明 -->
        <div v-if="currentPlatform === 'cyberbiz' && form.connectionType === 'api_polling'" class="guide-card">
          <h4><i class="ri-guide-line"></i> CYBERBIZ API 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>申請 CYBERBIZ API 帳號</strong>
              <p>前往 CYBERBIZ 後台 → 進階應用 → API，或聯繫 CYBERBIZ 系統客服申請</p>
            </li>
            <li>
              <strong>取得 API Username 和 Secret</strong>
              <p>申請通過後，取得 API Username（帳號）和 API Secret（密碼）</p>
            </li>
            <li>
              <strong>填入本頁表單</strong>
              <ul>
                <li>商店網域：填入 CYBERBIZ 商店網域（例如：<code class="code-inline">your-store.cyberbiz.co</code>）</li>
                <li>API Username：填入 API 帳號</li>
                <li>API Secret：填入 API 密碼</li>
              </ul>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            CYBERBIZ API 速率限制：每秒最多 5 個請求，每次上限 2 MB。API 功能需為企業版或 PLUS 方案。
          </div>
        </div>

        <!-- CYBERBIZ 狀態對照 -->
        <div v-if="currentPlatform === 'cyberbiz'" class="guide-card">
          <h4><i class="ri-exchange-line"></i> CYBERBIZ 訂單狀態對照</h4>
          <div class="status-table-wrapper">
            <table class="status-table">
              <thead>
                <tr>
                  <th>CYBERBIZ 狀態（financial_status）</th>
                  <th>系統狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(target, source) in cyberbizStatusMapping" :key="source">
                  <td><code>{{ source }}</code></td>
                  <td>{{ statusLabels[target] || target }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Shopee Webhook 設定說明 -->
        <div v-if="currentPlatform === 'shopee' && form.connectionType === 'webhook'" class="guide-card">
          <h4><i class="ri-guide-line"></i> 蝦皮 Shopee Webhook 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>登入蝦皮 Open Platform</strong>
              <p>前往 <a href="https://open.shopee.com" target="_blank">open.shopee.com</a> → 登入開發者帳號</p>
            </li>
            <li>
              <strong>建立或選擇 App</strong>
              <p>進入 My App → 選擇現有 App 或建立新 App（類型選 Shop Level）</p>
            </li>
            <li>
              <strong>設定 Webhook URL</strong>
              <ul>
                <li>前往 App 設定 → Webhook URL</li>
                <li>填入：<code class="code-inline">{{ form.webhookUrl }}</code></li>
                <li>訂閱事件：勾選 <strong>ORDER_STATUS_UPDATE</strong></li>
              </ul>
            </li>
            <li>
              <strong>填入 Partner Key 作為 Webhook Secret</strong>
              <p>在 My App → Basic Info 中找到 <strong>Partner Key</strong>，填入左側「Webhook Secret」欄位</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            蝦皮 Webhook 簽章使用 HMAC-SHA256（Hex 格式），以 Partner Key 為金鑰，本系統已支援自動驗證。
          </div>
        </div>

        <!-- Shopee API 輪詢設定說明 -->
        <div v-if="currentPlatform === 'shopee' && form.connectionType === 'api_polling'" class="guide-card">
          <h4><i class="ri-guide-line"></i> 蝦皮 Shopee Open API 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>取得 App 基本資訊</strong>
              <p>登入 <a href="https://open.shopee.com" target="_blank">open.shopee.com</a> → My App → 記錄 <strong>Partner ID</strong> 和 <strong>Partner Key</strong></p>
            </li>
            <li>
              <strong>完成商店授權取得 Access Token</strong>
              <p>透過 OAuth 2.0 授權流程取得 <strong>Access Token</strong> 和 <strong>Shop ID</strong>（可參考蝦皮 Open Platform 文件）</p>
            </li>
            <li>
              <strong>填入本頁表單</strong>
              <ul>
                <li>Shop ID：填入授權後取得的數字型商店 ID（例如：<code>1234567890</code>）</li>
                <li>Partner ID:Access Token：以冒號分隔，例如：<code>12345:your_access_token</code></li>
                <li>Partner Key：填入 App 的 Partner Key</li>
              </ul>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            蝦皮 Access Token 有效期約 4 小時，系統每次輪詢時自動使用當前 token 請求。若 token 過期請重新授權更新。
          </div>
        </div>

        <!-- Shopee 狀態對照 -->
        <div v-if="currentPlatform === 'shopee'" class="guide-card">
          <h4><i class="ri-exchange-line"></i> 蝦皮 Shopee 訂單狀態對照</h4>
          <div class="status-table-wrapper">
            <table class="status-table">
              <thead>
                <tr>
                  <th>Shopee 狀態（order_status）</th>
                  <th>系統狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(target, source) in shopeeStatusMapping" :key="source">
                  <td><code>{{ source }}</code></td>
                  <td>{{ statusLabels[target] || target }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- meepShop Webhook 設定說明 -->
        <div v-if="currentPlatform === 'meepshop' && form.connectionType === 'webhook'" class="guide-card">
          <h4><i class="ri-guide-line"></i> meepShop Webhook 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>取得 API 金鑰</strong>
              <p>登入 meepShop 後台 → 設定 → 擴充功能 → 開放 API → 點選「開始使用」取得 API Key</p>
            </li>
            <li>
              <strong>設定 Webhook</strong>
              <p>在同一頁面（開放 API 設定）找到 Webhook URL 設定欄位，填入：</p>
              <div style="background: #f0f0f0; padding: 10px; border-radius: 4px; font-size: 12px; margin-top: 6px; word-break: break-all;">
                {{ form.webhookUrl }}
              </div>
            </li>
            <li>
              <strong>填入 API Key 作為 Webhook Secret</strong>
              <p>將 meepShop API Key 填入左側「Webhook Secret」欄位（用於驗證 Webhook 推送簽章）</p>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            meepShop Open API 目前限時免費。若遇串接問題可聯繫 <a href="mailto:api@meepshop.com">api@meepshop.com</a>。
          </div>
        </div>

        <!-- meepShop API 輪詢設定說明 -->
        <div v-if="currentPlatform === 'meepshop' && form.connectionType === 'api_polling'" class="guide-card">
          <h4><i class="ri-guide-line"></i> meepShop API 設定步驟</h4>
          <ol class="guide-steps">
            <li>
              <strong>取得 API 金鑰</strong>
              <p>登入 meepShop 後台 → 設定 → 擴充功能 → 開放 API → 點選「開始使用」</p>
            </li>
            <li>
              <strong>記錄店鋪網域與 API Key</strong>
              <ul>
                <li>在後台 URL 或商店設定中確認您的店鋪識別碼</li>
                <li>複製頁面上的 <strong>API Key</strong></li>
              </ul>
            </li>
            <li>
              <strong>填入本頁表單</strong>
              <ul>
                <li>Shop Domain：填入您的 meepShop 店鋪識別碼（例如：<code>your-store</code>）</li>
                <li>API Key：填入上方複製的 API Key</li>
              </ul>
            </li>
          </ol>
          <div class="guide-note">
            <i class="ri-information-line"></i>
            meepShop 使用 GraphQL API，系統將定期查詢最新訂單。API 目前限時免費開放。
          </div>
        </div>

        <!-- meepShop 狀態對照 -->
        <div v-if="currentPlatform === 'meepshop'" class="guide-card">
          <h4><i class="ri-exchange-line"></i> meepShop 訂單狀態對照</h4>
          <div class="status-table-wrapper">
            <table class="status-table">
              <thead>
                <tr>
                  <th>meepShop 狀態</th>
                  <th>系統狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(target, source) in meepshopStatusMapping" :key="source">
                  <td><code>{{ source }}</code></td>
                  <td>{{ statusLabels[target] || target }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 通用說明（其他平台） -->
        <div v-if="!['woocommerce', 'shopify', 'shopline', '91app', 'cyberbiz'].includes(currentPlatform)" class="guide-card">
          <h4><i class="ri-information-line"></i> 連線方式說明</h4>
          <div class="guide-compare">
            <div class="guide-option">
              <div class="option-title">Webhook（即時）</div>
              <p>電商平台主動推送訂單到本系統，訂單幾乎即時同步。需要在電商平台後台設定 Webhook URL。</p>
            </div>
            <div class="guide-option">
              <div class="option-title">API 輪詢（定時）</div>
              <p>系統定時主動從電商平台拉取訂單，有一定延遲。適合平台不支援 Webhook 的情況。</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { ecomService } from '@/api/modules/ecom.js';
import { showError, showSuccess } from '@/utils/swal.js';

// WooCommerce 預設欄位映射
const WC_DEFAULT_FIELD_MAPPING = {
  orderId: 'id',
  orderNumber: 'number',
  customerEmail: 'billing.email',
  customerPhone: 'billing.phone',
  customerName: 'billing.first_name',
  orderDate: 'date_created',
  subtotal: 'subtotal',
  totalAmount: 'total',
  status: 'status'
};

// WooCommerce 預設狀態映射
const WC_DEFAULT_STATUS_MAPPING = {
  'pending': 'pending_payment',
  'processing': 'processing',
  'on-hold': 'pending',
  'completed': 'completed',
  'cancelled': 'cancelled',
  'refunded': 'refunded',
  'failed': 'cancelled'
};

// Shopify 預設欄位映射
const SHOPIFY_DEFAULT_FIELD_MAPPING = {
  orderId: 'id',
  orderNumber: 'order_number',
  customerEmail: 'email',
  customerPhone: 'phone',
  customerName: 'customer.first_name',
  orderDate: 'created_at',
  subtotal: 'subtotal_price',
  totalAmount: 'total_price',
  status: 'financial_status'
};

// Shopify 預設狀態映射
const SHOPIFY_DEFAULT_STATUS_MAPPING = {
  'pending': 'pending_payment',
  'authorized': 'processing',
  'partially_paid': 'processing',
  'paid': 'processing',
  'partially_refunded': 'processing',
  'refunded': 'refunded',
  'voided': 'cancelled'
};

// Shopline 預設欄位映射
const SHOPLINE_DEFAULT_FIELD_MAPPING = {
  orderId: 'id',
  orderNumber: 'order_number',
  customerEmail: 'buyer.email',
  customerPhone: 'buyer.phone',
  customerName: 'buyer.name',
  orderDate: 'created_at',
  subtotal: 'subtotal',
  totalAmount: 'total_price',
  status: 'status'
};

// Shopline 預設狀態映射
const SHOPLINE_DEFAULT_STATUS_MAPPING = {
  'pending': 'pending_payment',
  'confirmed': 'processing',
  'processing': 'processing',
  'shipped': 'shipped',
  'delivered': 'completed',
  'completed': 'completed',
  'cancelled': 'cancelled',
  'refunded': 'refunded'
};

// 91APP 預設欄位映射
const APP91_DEFAULT_FIELD_MAPPING = {
  orderId: 'SlaveId',
  orderNumber: 'SlaveNo',
  customerEmail: 'BuyerEmail',
  customerPhone: 'BuyerPhone',
  customerName: 'BuyerName',
  orderDate: 'OrderDateTime',
  subtotal: 'ItemTotal',
  totalAmount: 'TotalPrice',
  status: 'OrderStatus'
};

// 91APP 預設狀態映射
const APP91_DEFAULT_STATUS_MAPPING = {
  'WaitingToPay': 'pending_payment',
  'WaitingToCreditCheck': 'pending_payment',
  'WaitingToShipping': 'processing',
  'ConfirmedToShipping': 'processing',
  'WaitingToModifyStore': 'processing',
  'Finish': 'completed',
  'Cancel': 'cancelled'
};

// Shopee 預設欄位映射
const SHOPEE_DEFAULT_FIELD_MAPPING = {
  orderId: 'order_sn',
  orderNumber: 'order_sn',
  customerEmail: 'buyer_info.email',
  customerPhone: 'buyer_info.phone_number',
  customerName: 'buyer_info.name',
  orderDate: 'create_time',
  subtotal: 'total_amount',
  totalAmount: 'total_amount',
  status: 'order_status'
};

// Shopee 預設狀態映射
const SHOPEE_DEFAULT_STATUS_MAPPING = {
  'UNPAID': 'pending_payment',
  'READY_TO_SHIP': 'processing',
  'PROCESSED': 'shipped',
  'SHIPPED': 'shipped',
  'COMPLETED': 'completed',
  'CANCELLED': 'cancelled',
  'IN_CANCEL': 'cancelled',
  'TO_RETURN': 'refunded'
};

// meepShop 預設欄位映射
const MEEPSHOP_DEFAULT_FIELD_MAPPING = {
  orderId: 'id',
  orderNumber: 'orderNo',
  customerEmail: 'buyer.email',
  customerPhone: 'buyer.mobile',
  customerName: 'buyer.name',
  orderDate: 'createdAt',
  subtotal: 'priceInfo.total',
  totalAmount: 'priceInfo.total',
  status: 'status'
};

// meepShop 預設狀態映射
const MEEPSHOP_DEFAULT_STATUS_MAPPING = {
  'UNPAID': 'pending_payment',
  'PAID': 'processing',
  'PROCESSING': 'processing',
  'SHIPPED': 'shipped',
  'DONE': 'completed',
  'CANCELLED': 'cancelled',
  'REFUNDING': 'refunded',
  'REFUNDED': 'refunded'
};

// CYBERBIZ 預設欄位映射
const CYBERBIZ_DEFAULT_FIELD_MAPPING = {
  orderId: 'id',
  orderNumber: 'order_number',
  customerEmail: 'email',
  customerPhone: 'customer.phone',
  customerName: 'customer.name',
  orderDate: 'created_at',
  subtotal: 'subtotal_price',
  totalAmount: 'total_price',
  status: 'financial_status'
};

// CYBERBIZ 預設狀態映射
const CYBERBIZ_DEFAULT_STATUS_MAPPING = {
  'pending': 'pending_payment',
  'authorized': 'processing',
  'partially_paid': 'processing',
  'paid': 'processing',
  'partially_refunded': 'processing',
  'refunded': 'refunded',
  'voided': 'cancelled'
};

const PLATFORM_DEFAULTS = {
  woocommerce: {
    fieldMapping: WC_DEFAULT_FIELD_MAPPING,
    statusMapping: WC_DEFAULT_STATUS_MAPPING,
    webhookEvents: ['order.created', 'order.updated']
  },
  shopify: {
    fieldMapping: SHOPIFY_DEFAULT_FIELD_MAPPING,
    statusMapping: SHOPIFY_DEFAULT_STATUS_MAPPING,
    webhookEvents: ['orders/create', 'orders/updated', 'orders/paid', 'orders/cancelled', 'orders/fulfilled']
  },
  shopline: {
    fieldMapping: SHOPLINE_DEFAULT_FIELD_MAPPING,
    statusMapping: SHOPLINE_DEFAULT_STATUS_MAPPING,
    webhookEvents: ['order_created', 'order_updated', 'order_paid', 'order_cancelled']
  },
  '91app': {
    fieldMapping: APP91_DEFAULT_FIELD_MAPPING,
    statusMapping: APP91_DEFAULT_STATUS_MAPPING,
    webhookEvents: ['order.created', 'order.status_changed', 'order.cancelled']
  },
  cyberbiz: {
    fieldMapping: CYBERBIZ_DEFAULT_FIELD_MAPPING,
    statusMapping: CYBERBIZ_DEFAULT_STATUS_MAPPING,
    webhookEvents: ['orders/create', 'orders/paid', 'orders/cancel', 'orders/update']
  },
  shopee: {
    fieldMapping: SHOPEE_DEFAULT_FIELD_MAPPING,
    statusMapping: SHOPEE_DEFAULT_STATUS_MAPPING,
    webhookEvents: ['ORDER_STATUS_UPDATE', 'ORDER_CANCEL_REASON_UPDATED']
  },
  meepshop: {
    fieldMapping: MEEPSHOP_DEFAULT_FIELD_MAPPING,
    statusMapping: MEEPSHOP_DEFAULT_STATUS_MAPPING,
    webhookEvents: ['ORDER_CREATED', 'ORDER_STATUS_UPDATED', 'ORDER_PAID']
  }
};

const STATUS_LABELS = {
  'pending': '待處理',
  'pending_payment': '待付款',
  'processing': '準備出貨',
  'completed': '已下單',
  'shipped': '已出貨',
  'cancelled': '已取消',
  'refunded': '已退款',
  'failed': '失敗'
};

// WooCommerce 欄位映射視覺化說明表
const WC_FIELD_MAPPING_ROWS = [
  { key: 'orderId',       desc: '訂單 ID（唯一識別碼）', path: 'id',                 example: '12345' },
  { key: 'orderNumber',   desc: '訂單編號（顯示用）',   path: 'number',             example: '#12345' },
  { key: 'customerEmail', desc: '顧客電子郵件',         path: 'billing.email',       example: 'user@example.com' },
  { key: 'customerPhone', desc: '顧客手機號碼',         path: 'billing.phone',       example: '0912345678' },
  { key: 'customerName',  desc: '顧客姓名',             path: 'billing.first_name',  example: '王小明' },
  { key: 'orderDate',     desc: '訂單成立時間',         path: 'date_created',        example: '2024-01-01T10:00:00' },
  { key: 'subtotal',      desc: '商品小計（不含運費）', path: 'subtotal',            example: '490.00' },
  { key: 'totalAmount',   desc: '訂單總金額',           path: 'total',               example: '550.00' },
  { key: 'status',        desc: '訂單狀態',             path: 'status',              example: 'processing' }
];

// WooCommerce 狀態映射視覺化說明表
const WC_STATUS_MAPPING_ROWS = [
  { wc: 'pending',    wcDesc: '待付款',    system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'processing', wcDesc: '處理中',    system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'on-hold',    wcDesc: '保留中',    system: 'pending',         systemDesc: '待處理' },
  { wc: 'completed',  wcDesc: '已完成',    system: 'completed',       systemDesc: '已下單' },
  { wc: 'cancelled',  wcDesc: '已取消',    system: 'cancelled',       systemDesc: '已取消' },
  { wc: 'refunded',   wcDesc: '已退款',    system: 'refunded',        systemDesc: '已退款' },
  { wc: 'failed',     wcDesc: '付款失敗',  system: 'cancelled',       systemDesc: '已取消' }
];

// Shopify 欄位映射視覺化說明表
const SHOPIFY_FIELD_MAPPING_ROWS = [
  { key: 'orderId',       desc: '訂單 ID（唯一識別碼）', path: 'id',                   example: '820982911946154508' },
  { key: 'orderNumber',   desc: '訂單編號（顯示用）',   path: 'order_number',          example: '1001' },
  { key: 'customerEmail', desc: '顧客電子郵件',         path: 'email',                 example: 'user@example.com' },
  { key: 'customerPhone', desc: '顧客手機號碼',         path: 'phone',                 example: '+886912345678' },
  { key: 'customerName',  desc: '顧客姓名',             path: 'customer.first_name',   example: '小明' },
  { key: 'orderDate',     desc: '訂單成立時間',         path: 'created_at',            example: '2024-01-01T10:00:00+08:00' },
  { key: 'subtotal',      desc: '商品小計（不含運費）', path: 'subtotal_price',        example: '490.00' },
  { key: 'totalAmount',   desc: '訂單總金額',           path: 'total_price',           example: '550.00' },
  { key: 'status',        desc: '付款狀態',             path: 'financial_status',      example: 'paid' }
];

// Shopify 狀態映射視覺化說明表
const SHOPIFY_STATUS_MAPPING_ROWS = [
  { wc: 'pending',            wcDesc: '待付款',     system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'authorized',         wcDesc: '已授權',     system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'partially_paid',     wcDesc: '部分付款',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'paid',               wcDesc: '已付款',     system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'partially_refunded', wcDesc: '部分退款',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'refunded',           wcDesc: '已退款',     system: 'refunded',        systemDesc: '已退款' },
  { wc: 'voided',             wcDesc: '已作廢',     system: 'cancelled',       systemDesc: '已取消' }
];

// Shopline 欄位映射視覺化說明表
const SHOPLINE_FIELD_MAPPING_ROWS = [
  { key: 'orderId',       desc: '訂單 ID（唯一識別碼）', path: 'id',              example: 'sl_order_001' },
  { key: 'orderNumber',   desc: '訂單編號（顯示用）',   path: 'order_number',    example: 'SL-20240101-001' },
  { key: 'customerEmail', desc: '顧客電子郵件',         path: 'buyer.email',     example: 'user@example.com' },
  { key: 'customerPhone', desc: '顧客手機號碼',         path: 'buyer.phone',     example: '0912345678' },
  { key: 'customerName',  desc: '顧客姓名',             path: 'buyer.name',      example: '王小明' },
  { key: 'orderDate',     desc: '訂單成立時間',         path: 'created_at',      example: '2024-01-01T10:00:00Z' },
  { key: 'subtotal',      desc: '商品小計（不含運費）', path: 'subtotal',        example: '490' },
  { key: 'totalAmount',   desc: '訂單總金額',           path: 'total_price',     example: '550' },
  { key: 'status',        desc: '訂單狀態',             path: 'status',          example: 'confirmed' }
];

// 91APP 欄位映射視覺化說明表
const APP91_FIELD_MAPPING_ROWS = [
  { key: 'orderId',       desc: 'TS 子單 ID（唯一識別碼）', path: 'SlaveId',       example: '1234567' },
  { key: 'orderNumber',   desc: 'TS 子單編號（顯示用）',    path: 'SlaveNo',       example: 'TS-20240101-001' },
  { key: 'customerEmail', desc: '購買人電子郵件',           path: 'BuyerEmail',    example: 'user@example.com' },
  { key: 'customerPhone', desc: '購買人手機號碼',           path: 'BuyerPhone',    example: '0912345678' },
  { key: 'customerName',  desc: '購買人姓名',               path: 'BuyerName',     example: '王小明' },
  { key: 'orderDate',     desc: '訂單成立時間',             path: 'OrderDateTime', example: '2024-01-01 10:00:00' },
  { key: 'subtotal',      desc: '商品小計',                 path: 'ItemTotal',     example: '490' },
  { key: 'totalAmount',   desc: '訂單總金額',               path: 'TotalPrice',    example: '550' },
  { key: 'status',        desc: '訂單狀態',                 path: 'OrderStatus',   example: 'Finish' }
];

// 91APP 狀態映射視覺化說明表
const APP91_STATUS_MAPPING_ROWS = [
  { wc: 'WaitingToPay',          wcDesc: '待付款',            system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'WaitingToCreditCheck',  wcDesc: '付款確認中',        system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'WaitingToShipping',     wcDesc: '已成立（待出貨）',  system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'ConfirmedToShipping',   wcDesc: '已確認待出貨',      system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'WaitingToModifyStore',  wcDesc: '門市閉店待重選',    system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'Finish',                wcDesc: '已完成',            system: 'completed',       systemDesc: '已下單' },
  { wc: 'Cancel',                wcDesc: '已取消',            system: 'cancelled',       systemDesc: '已取消' }
];

// CYBERBIZ 欄位映射視覺化說明表
const CYBERBIZ_FIELD_MAPPING_ROWS = [
  { key: 'orderId',       desc: '訂單 ID（唯一識別碼）', path: 'id',               example: '123456' },
  { key: 'orderNumber',   desc: '訂單編號（顯示用）',   path: 'order_number',     example: 'CB-20240101-001' },
  { key: 'customerEmail', desc: '顧客電子郵件',         path: 'email',            example: 'user@example.com' },
  { key: 'customerPhone', desc: '顧客手機號碼',         path: 'customer.phone',   example: '0912345678' },
  { key: 'customerName',  desc: '顧客姓名',             path: 'customer.name',    example: '王小明' },
  { key: 'orderDate',     desc: '訂單成立時間',         path: 'created_at',       example: '2024-01-01T10:00:00+08:00' },
  { key: 'subtotal',      desc: '商品小計（不含運費）', path: 'subtotal_price',   example: '490' },
  { key: 'totalAmount',   desc: '訂單總金額',           path: 'total_price',      example: '550' },
  { key: 'status',        desc: '付款狀態',             path: 'financial_status', example: 'paid' }
];

// CYBERBIZ 狀態映射視覺化說明表
const CYBERBIZ_STATUS_MAPPING_ROWS = [
  { wc: 'pending',            wcDesc: '待付款',     system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'authorized',         wcDesc: '已授權',     system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'partially_paid',     wcDesc: '部分付款',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'paid',               wcDesc: '已付款',     system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'partially_refunded', wcDesc: '部分退款',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'refunded',           wcDesc: '已退款',     system: 'refunded',        systemDesc: '已退款' },
  { wc: 'voided',             wcDesc: '已作廢',     system: 'cancelled',       systemDesc: '已取消' }
];

// Shopee 欄位映射視覺化說明表
const SHOPEE_FIELD_MAPPING_ROWS = [
  { key: 'orderId',       desc: '訂單編號（唯一識別碼）', path: 'order_sn',               example: '220101ABCDEF' },
  { key: 'orderNumber',   desc: '訂單編號（顯示用）',   path: 'order_sn',               example: '220101ABCDEF' },
  { key: 'customerEmail', desc: '顧客電子郵件',         path: 'buyer_info.email',        example: 'user@example.com' },
  { key: 'customerPhone', desc: '顧客手機號碼',         path: 'buyer_info.phone_number', example: '0912345678' },
  { key: 'customerName',  desc: '買家帳號名稱',         path: 'buyer_info.name',         example: 'buyer123' },
  { key: 'orderDate',     desc: '訂單成立時間（Unix）', path: 'create_time',             example: '1640995200' },
  { key: 'subtotal',      desc: '訂單金額',             path: 'total_amount',            example: '550' },
  { key: 'totalAmount',   desc: '訂單總金額',           path: 'total_amount',            example: '550' },
  { key: 'status',        desc: '訂單狀態',             path: 'order_status',            example: 'READY_TO_SHIP' }
];

// Shopee 狀態映射視覺化說明表
const SHOPEE_STATUS_MAPPING_ROWS = [
  { wc: 'UNPAID',           wcDesc: '未付款',       system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'READY_TO_SHIP',    wcDesc: '等待出貨',     system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'PROCESSED',        wcDesc: '已出貨確認',   system: 'shipped',         systemDesc: '已出貨' },
  { wc: 'SHIPPED',          wcDesc: '已出貨',       system: 'shipped',         systemDesc: '已出貨' },
  { wc: 'COMPLETED',        wcDesc: '已完成',       system: 'completed',       systemDesc: '已下單' },
  { wc: 'CANCELLED',        wcDesc: '已取消',       system: 'cancelled',       systemDesc: '已取消' },
  { wc: 'IN_CANCEL',        wcDesc: '取消申請中',   system: 'cancelled',       systemDesc: '已取消' },
  { wc: 'TO_RETURN',        wcDesc: '退貨/退款中',  system: 'refunded',        systemDesc: '已退款' }
];

// meepShop 欄位映射視覺化說明表
const MEEPSHOP_FIELD_MAPPING_ROWS = [
  { key: 'orderId',       desc: '訂單 ID（唯一識別碼）', path: 'id',               example: 'ms-order-uuid' },
  { key: 'orderNumber',   desc: '訂單編號（顯示用）',   path: 'orderNo',          example: 'MS-20240101-001' },
  { key: 'customerEmail', desc: '顧客電子郵件',         path: 'buyer.email',      example: 'user@example.com' },
  { key: 'customerPhone', desc: '顧客手機號碼',         path: 'buyer.mobile',     example: '0912345678' },
  { key: 'customerName',  desc: '顧客姓名',             path: 'buyer.name',       example: '王小明' },
  { key: 'orderDate',     desc: '訂單成立時間',         path: 'createdAt',        example: '2024-01-01T10:00:00Z' },
  { key: 'subtotal',      desc: '商品小計',             path: 'priceInfo.total',  example: '490' },
  { key: 'totalAmount',   desc: '訂單總金額',           path: 'priceInfo.total',  example: '550' },
  { key: 'status',        desc: '訂單狀態',             path: 'status',           example: 'PAID' }
];

// meepShop 狀態映射視覺化說明表
const MEEPSHOP_STATUS_MAPPING_ROWS = [
  { wc: 'UNPAID',      wcDesc: '未付款',   system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'PAID',        wcDesc: '已付款',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'PROCESSING',  wcDesc: '出貨中',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'SHIPPED',     wcDesc: '已出貨',   system: 'shipped',         systemDesc: '已出貨' },
  { wc: 'DONE',        wcDesc: '已完成',   system: 'completed',       systemDesc: '已下單' },
  { wc: 'CANCELLED',   wcDesc: '已取消',   system: 'cancelled',       systemDesc: '已取消' },
  { wc: 'REFUNDING',   wcDesc: '退款中',   system: 'refunded',        systemDesc: '已退款' },
  { wc: 'REFUNDED',    wcDesc: '已退款',   system: 'refunded',        systemDesc: '已退款' }
];

// Shopline 狀態映射視覺化說明表
const SHOPLINE_STATUS_MAPPING_ROWS = [
  { wc: 'pending',    wcDesc: '待確認',   system: 'pending_payment', systemDesc: '待付款' },
  { wc: 'confirmed',  wcDesc: '已確認',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'processing', wcDesc: '處理中',   system: 'processing',      systemDesc: '準備出貨' },
  { wc: 'shipped',    wcDesc: '已出貨',   system: 'shipped',         systemDesc: '已出貨' },
  { wc: 'delivered',  wcDesc: '已送達',   system: 'completed',       systemDesc: '已下單' },
  { wc: 'completed',  wcDesc: '已完成',   system: 'completed',       systemDesc: '已下單' },
  { wc: 'cancelled',  wcDesc: '已取消',   system: 'cancelled',       systemDesc: '已取消' },
  { wc: 'refunded',   wcDesc: '已退款',   system: 'refunded',        systemDesc: '已退款' }
];

export default {
  name: 'EcomConfigEdit',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const configId = route.params.configId;
    const platform = route.params.platform;

    const getPlatformDisplayName = (code) => {
      const names = {
        shopify: 'Shopify',
        woocommerce: 'WooCommerce',
        shopline: 'Shopline',
        '91app': '91APP',
        cyberbiz: 'CYBERBIZ',
        shopee: '蝦皮 Shopee',
        meepshop: 'meepShop',
        others: '其他'
      };
      return names[code] || code;
    };

    const isEdit = ref(!!configId);
    const currentPlatform = ref(platform || '');
    const { pageTitle } = usePageTitle(
      isEdit.value ? '編輯電商平台設定' : `新增 ${getPlatformDisplayName(platform)} 設定`
    );

    const loading = ref(false);
    const submitting = ref(false);
    const polling = ref(false);
    const pollResult = ref(null);
    const copied = ref(false);
    const showSecret = ref(false);
    const showMappingEditor = ref(false);
    const showRawJson = ref(false);
    const fieldMappingError = ref('');
    const statusMappingError = ref('');

    const wcFieldMappingRows = WC_FIELD_MAPPING_ROWS;
    const wcStatusMappingRows = WC_STATUS_MAPPING_ROWS;
    const shopifyFieldMappingRows = SHOPIFY_FIELD_MAPPING_ROWS;
    const shopifyStatusMappingRows = SHOPIFY_STATUS_MAPPING_ROWS;
    const shoplineFieldMappingRows = SHOPLINE_FIELD_MAPPING_ROWS;
    const shoplineStatusMappingRows = SHOPLINE_STATUS_MAPPING_ROWS;
    const app91FieldMappingRows = APP91_FIELD_MAPPING_ROWS;
    const app91StatusMappingRows = APP91_STATUS_MAPPING_ROWS;

    const form = reactive({
      id: null,
      platform: platform || '',
      platformName: '',
      configIdentifier: '',
      connectionType: 'webhook',
      webhookUrl: '',
      webhookSecret: '',
      webhookEvents: [],
      apiUrl: '',
      apiKey: '',
      apiSecret: '',
      pollInterval: 15,
      isActive: true,
      fieldMapping: {},
      statusMapping: {}
    });

    // JSON 編輯器 computed values
    const fieldMappingJson = computed({
      get: () => JSON.stringify(form.fieldMapping, null, 2),
      set: (val) => {
        try {
          form.fieldMapping = JSON.parse(val);
          fieldMappingError.value = '';
        } catch {
          fieldMappingError.value = 'JSON 格式錯誤，請修正後再儲存';
        }
      }
    });

    const statusMappingJson = computed({
      get: () => JSON.stringify(form.statusMapping, null, 2),
      set: (val) => {
        try {
          form.statusMapping = JSON.parse(val);
          statusMappingError.value = '';
        } catch {
          statusMappingError.value = 'JSON 格式錯誤，請修正後再儲存';
        }
      }
    });

    const validateFieldMappingJson = () => {
      try {
        JSON.parse(JSON.stringify(form.fieldMapping));
        fieldMappingError.value = '';
      } catch {
        fieldMappingError.value = 'JSON 格式錯誤';
      }
    };

    const validateStatusMappingJson = () => {
      try {
        JSON.parse(JSON.stringify(form.statusMapping));
        statusMappingError.value = '';
      } catch {
        statusMappingError.value = 'JSON 格式錯誤';
      }
    };

    const platformNamePlaceholder = computed(() => {
      const map = {
        woocommerce: '例如：我的 WooCommerce 商店',
        shopify: '例如：Shopify 商店',
        shopline: '例如：Shopline 商店',
        '91app': '例如：91APP 官方商店',
        cyberbiz: '例如：CYBERBIZ 官方商店',
        shopee: '例如：蝦皮官方商店',
        meepshop: '例如：meepShop 品牌商店'
      };
      return map[currentPlatform.value] || '例如：電商平台名稱';
    });

    const connectionTypeHint = computed(() => {
      if (form.connectionType === 'webhook') {
        return '電商平台主動推送訂單，即時同步，推薦使用';
      }
      if (form.connectionType === 'api_polling') {
        return '系統定時主動拉取訂單，適合平台不支援 Webhook 的情況';
      }
      return '手動上傳訂單檔案';
    });

    const webhookUrlHint = computed(() => {
      const hints = {
        woocommerce: '將此 URL 貼至 WooCommerce 後台 → WooCommerce → 設定 → 進階 → Webhooks',
        shopify: '將此 URL 貼至 Shopify 後台 → Settings → Notifications → Webhooks',
        shopline: '將此 URL 填入 Shopline Open API 建立 Webhook 的 address 欄位',
        '91app': '提供此 URL 給 91APP 客服（hd@91apptw.zohodesk.com）申請 Webhook 開通',
        cyberbiz: '將此 URL 貼至 CYBERBIZ 後台 → 進階應用 → Webhook → 新增',
        shopee: '將此 URL 填入蝦皮 Open Platform → My App → Webhook URL 設定',
        meepshop: '將此 URL 填入 meepShop 後台 → 設定 → 擴充功能 → 開放 API → Webhook URL'
      };
      return hints[currentPlatform.value] || '將此 URL 貼至電商平台後台的 Webhook 設定頁面';
    });

    const webhookSecretHint = computed(() => {
      const hints = {
        woocommerce: '此 Secret 需同時填入 WooCommerce Webhook 的「Secret」欄位',
        shopify: 'Shopify 建立 Webhook 後會顯示 Signing Secret，請將該值填入此欄位',
        shopline: '此 Secret 用於驗證 Shopline 推送的 Webhook 簽章',
        '91app': '91APP 客服開通 Webhook 後會提供 Shared Secret，填入此欄位',
        cyberbiz: '將 CYBERBIZ 後台 Webhook 設定頁面中的 HMAC Key 填入此欄位',
        shopee: '填入蝦皮 Open Platform → My App → Basic Info 中的 Partner Key',
        meepshop: '填入 meepShop 後台取得的 API Key（作為 Webhook 簽章驗證金鑰）'
      };
      return hints[currentPlatform.value] || '此 Secret 用於驗證電商平台推送的 Webhook 簽章';
    });

    // API 輪詢欄位動態設定（依平台調整標籤和提示文字）
    const apiUrlLabel = computed(() => {
      const labels = {
        woocommerce: '網站根網址',
        shopify: 'Shop URL',
        shopline: 'Shop Handle（商店代碼）',
        '91app': 'ShopId（商店 ID）',
        cyberbiz: '商店網域',
        shopee: 'Shop ID',
        meepshop: 'Shop Domain（店鋪識別碼）'
      };
      return labels[currentPlatform.value] || 'API URL';
    });

    const apiUrlPlaceholder = computed(() => {
      const placeholders = {
        woocommerce: 'https://your-store.com',
        shopify: 'your-store.myshopify.com',
        shopline: 'your-store-handle',
        '91app': '12345',
        cyberbiz: 'your-store.cyberbiz.co',
        shopee: '1234567890',
        meepshop: 'your-store'
      };
      return placeholders[currentPlatform.value] || 'https://your-store.com';
    });

    const apiUrlHint = computed(() => {
      const hints = {
        woocommerce: 'WooCommerce 網站根網址（系統將自動呼叫 /wp-json/wc/v3/orders）',
        shopify: '填入 *.myshopify.com 格式的網址（不含 https://），系統將呼叫 Admin API',
        shopline: '填入您的 Shopline 商店代碼（handle），可在後台查詢',
        '91app': '填入數字型的 91APP 商店 ID（ShopId），可在 91APP OMNI 後台商店設定中查詢',
        cyberbiz: '填入您的 CYBERBIZ 商店網域（例如 your-store.cyberbiz.co），系統將呼叫 CYBERBIZ API',
        shopee: '填入授權後取得的數字型蝦皮商店 ID（Shop ID）',
        meepshop: '填入您的 meepShop 店鋪識別碼（可在後台網址列查詢）'
      };
      return hints[currentPlatform.value] || 'API 端點或商店識別碼';
    });

    const apiKeyLabel = computed(() => {
      const labels = {
        woocommerce: 'Consumer Key',
        shopify: 'Access Token',
        shopline: 'Access Token',
        '91app': 'x-api-key',
        cyberbiz: 'API Username',
        shopee: 'Partner ID:Access Token',
        meepshop: 'API Key'
      };
      return labels[currentPlatform.value] || 'API Key';
    });

    const apiKeyPlaceholder = computed(() => {
      const placeholders = {
        woocommerce: 'ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        shopify: 'shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        shopline: '請輸入 Shopline Access Token',
        '91app': '請輸入 91APP x-api-key',
        cyberbiz: '請輸入 CYBERBIZ API Username',
        shopee: '12345:your_access_token',
        meepshop: '請輸入 meepShop API Key'
      };
      return placeholders[currentPlatform.value] || '請輸入 API Key';
    });

    const apiKeyHint = computed(() => {
      const hints = {
        woocommerce: 'WooCommerce 後台 → WooCommerce → 設定 → 進階 → REST API → 新增金鑰',
        shopify: 'Shopify 後台 → Settings → Apps → Develop apps → 建立 Custom App 後取得',
        shopline: 'Shopline 後台 → 應用程式 → Open API → 複製 Access Token',
        '91app': '透過 91APP OMNI → 公司資訊與方案 → 服務申請與資料異動 申請取得',
        cyberbiz: '聯繫 CYBERBIZ 系統客服申請 API 帳號，或於後台 → 進階應用 → API 取得',
        shopee: '格式：{Partner ID}:{Access Token}，以冒號分隔。Partner ID 在 Open Platform My App → Basic Info 取得',
        meepshop: 'meepShop 後台 → 設定 → 擴充功能 → 開放 API → 取得 API Key'
      };
      return hints[currentPlatform.value] || '請參考平台文件取得 API Key';
    });

    const apiSecretLabel = computed(() => {
      const labels = {
        woocommerce: 'Consumer Secret',
        cyberbiz: 'API Secret',
        shopee: 'Partner Key'
      };
      return labels[currentPlatform.value] || 'API Secret';
    });

    const apiSecretPlaceholder = computed(() => {
      const placeholders = {
        woocommerce: 'cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        cyberbiz: '請輸入 CYBERBIZ API Secret',
        shopee: '請輸入蝦皮 Open Platform Partner Key'
      };
      return placeholders[currentPlatform.value] || '請輸入 API Secret';
    });

    // Shopify/Shopline/91APP/meepShop 使用單一 key，不需要 secret 欄位
    const showApiSecretField = computed(() => {
      return !['shopify', 'shopline', '91app', 'meepshop'].includes(currentPlatform.value);
    });

    const wooStatusMapping = computed(() => WC_DEFAULT_STATUS_MAPPING);
    const shopifyStatusMapping = computed(() => SHOPIFY_DEFAULT_STATUS_MAPPING);
    const shoplineStatusMapping = computed(() => SHOPLINE_DEFAULT_STATUS_MAPPING);
    const app91StatusMapping = computed(() => APP91_DEFAULT_STATUS_MAPPING);
    const cyberbizStatusMapping = computed(() => CYBERBIZ_DEFAULT_STATUS_MAPPING);
    const shopeeStatusMapping = computed(() => SHOPEE_DEFAULT_STATUS_MAPPING);
    const meepshopStatusMapping = computed(() => MEEPSHOP_DEFAULT_STATUS_MAPPING);
    const statusLabels = computed(() => STATUS_LABELS);

    const cyberbizFieldMappingRows = CYBERBIZ_FIELD_MAPPING_ROWS;
    const cyberbizStatusMappingRows = CYBERBIZ_STATUS_MAPPING_ROWS;
    const shopeeFieldMappingRows = SHOPEE_FIELD_MAPPING_ROWS;
    const shopeeStatusMappingRows = SHOPEE_STATUS_MAPPING_ROWS;
    const meepshopFieldMappingRows = MEEPSHOP_FIELD_MAPPING_ROWS;
    const meepshopStatusMappingRows = MEEPSHOP_STATUS_MAPPING_ROWS;

    // 套用平台預設值
    const applyPlatformDefaults = (platformCode, isNewConfig = false) => {
      const defaults = PLATFORM_DEFAULTS[platformCode];
      if (defaults) {
        if (!Object.keys(form.fieldMapping).length) {
          form.fieldMapping = { ...defaults.fieldMapping };
        }
        if (!Object.keys(form.statusMapping).length) {
          form.statusMapping = { ...defaults.statusMapping };
        }
        // 新建設定時套用預設勾選事件
        if (isNewConfig && defaults.webhookEvents && !form.webhookEvents.length) {
          form.webhookEvents = [...defaults.webhookEvents];
        }
      }
    };

    const fetchConfig = async () => {
      if (!isEdit.value && platform) {
        if (platform === 'pos') {
          router.push({ name: 'ClientWixtarConfigEdit' });
          return;
        }

        form.platform = platform;
        currentPlatform.value = platform;
        form.webhookUrl = `${window.location.origin}/api/v1/ecom/webhooks/${platform}/orders`;
        applyPlatformDefaults(platform, true);
        return;
      }

      if (!configId) return;

      loading.value = true;
      try {
        const response = await ecomService.getPlatformConfig(configId);
        if (response && response.success && response.data) {
          const data = response.data;
          form.id = data.id;
          form.platform = data.platform;
          currentPlatform.value = data.platform;
          form.platformName = data.platformName || '';
          form.configIdentifier = data.configIdentifier || '';
          form.connectionType = data.connectionType || 'webhook';
          form.webhookUrl = data.webhookUrl || `${window.location.origin}/api/v1/ecom/webhooks/${data.platform}/orders`;
          form.webhookSecret = data.webhookSecret || '';
          form.webhookEvents = data.webhookEvents || [];
          form.apiUrl = data.apiUrl || '';
          form.apiKey = data.apiKey || '';
          form.apiSecret = data.apiSecret || '';
          form.pollInterval = data.pollInterval || 15;
          form.isActive = data.isActive !== undefined ? data.isActive : true;
          form.fieldMapping = data.fieldMapping || {};
          form.statusMapping = data.statusMapping || {};

          // 補齊空的 mapping
          applyPlatformDefaults(data.platform);
        }
      } catch (err) {
        console.error('獲取設定失敗:', err);
        await showError('載入失敗', err.message || '獲取設定失敗');
      } finally {
        loading.value = false;
      }
    };

    const generateSecret = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const length = 40;
      let result = '';
      const array = new Uint8Array(length);
      crypto.getRandomValues(array);
      for (let i = 0; i < length; i++) {
        result += chars[array[i] % chars.length];
      }
      form.webhookSecret = result;
      showSecret.value = true;
    };

    const copyWebhookUrl = async () => {
      try {
        await navigator.clipboard.writeText(form.webhookUrl);
        copied.value = true;
        setTimeout(() => { copied.value = false; }, 2000);
      } catch {
        await showError('複製失敗', '請手動複製上方的 URL');
      }
    };

    const handleSubmit = async () => {
      if (!form.platformName || !form.connectionType || !form.platform) {
        await showError('驗證失敗', '請填寫所有必填欄位');
        return;
      }

      if (form.connectionType === 'webhook' && !form.webhookSecret) {
        await showError('驗證失敗', '請填寫 Webhook Secret');
        return;
      }

      if (form.connectionType === 'api_polling') {
        const needSecret = !['shopify', 'shopline', '91app'].includes(form.platform);
        if (!form.apiUrl || !form.apiKey || (needSecret && !form.apiSecret)) {
          const msg = needSecret
            ? `請填寫 ${apiUrlLabel.value}、${apiKeyLabel.value} 和 ${apiSecretLabel.value}`
            : `請填寫 ${apiUrlLabel.value} 和 ${apiKeyLabel.value}`;
          await showError('驗證失敗', msg);
          return;
        }
      }

      if (fieldMappingError.value || statusMappingError.value) {
        await showError('驗證失敗', '欄位映射或狀態映射 JSON 格式錯誤，請修正後再儲存');
        return;
      }

      submitting.value = true;
      try {
        const submitData = {
          ...form,
          fieldMapping: form.fieldMapping || {},
          statusMapping: form.statusMapping || {}
        };

        if (isEdit.value && form.id) {
          submitData.id = form.id;
        }

        const response = await ecomService.setPlatformConfig(form.platform, submitData);
        if (response && response.success) {
          await showSuccess('儲存成功', '電商平台設定已儲存');
          router.push({ name: 'ClientEcomConfig' });
        } else {
          await showError('儲存失敗', response?.message || '儲存設定失敗');
        }
      } catch (err) {
        console.error('儲存設定失敗:', err);
        await showError('儲存失敗', err.message || '儲存設定失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleManualPoll = async () => {
      polling.value = true;
      pollResult.value = null;
      try {
        const response = await ecomService.pollPlatformOrders(form.platform, form.id);
        if (response && response.success) {
          pollResult.value = {
            success: true,
            ...response.data
          };
        }
      } catch (err) {
        await showError('輪詢失敗', err.message || '執行 API 輪詢時發生錯誤');
      } finally {
        polling.value = false;
      }
    };

    const handleBack = () => {
      router.push({ name: 'ClientEcomConfig' });
    };

    onMounted(() => {
      fetchConfig();
    });

    return {
      pageTitle,
      loading,
      submitting,
      polling,
      pollResult,
      copied,
      showSecret,
      showMappingEditor,
      showRawJson,
      fieldMappingJson,
      statusMappingJson,
      fieldMappingError,
      statusMappingError,
      form,
      isEdit,
      currentPlatform,
      platformNamePlaceholder,
      connectionTypeHint,
      webhookUrlHint,
      webhookSecretHint,
      apiUrlLabel,
      apiUrlPlaceholder,
      apiUrlHint,
      apiKeyLabel,
      apiKeyPlaceholder,
      apiKeyHint,
      apiSecretLabel,
      apiSecretPlaceholder,
      showApiSecretField,
      wooStatusMapping,
      shopifyStatusMapping,
      shoplineStatusMapping,
      statusLabels,
      wcFieldMappingRows,
      wcStatusMappingRows,
      shopifyFieldMappingRows,
      shopifyStatusMappingRows,
      shoplineFieldMappingRows,
      shoplineStatusMappingRows,
      app91FieldMappingRows,
      app91StatusMappingRows,
      app91StatusMapping,
      cyberbizFieldMappingRows,
      cyberbizStatusMappingRows,
      cyberbizStatusMapping,
      shopeeFieldMappingRows,
      shopeeStatusMappingRows,
      shopeeStatusMapping,
      meepshopFieldMappingRows,
      meepshopStatusMappingRows,
      meepshopStatusMapping,
      validateFieldMappingJson,
      validateStatusMappingJson,
      generateSecret,
        copyWebhookUrl,
      handleSubmit,
      handleManualPoll,
      handleBack,
      getPlatformDisplayName
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

/* 雙欄版面 */
.form-layout {
  display: grid;
  grid-template-columns: minmax(0, 680px) minmax(0, 360px);
  gap: 24px;
  align-items: start;
}

@media (max-width: 1100px) {
  .form-layout {
    grid-template-columns: 1fr;
  }
}

/* 左側表單 */
.form-container {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 24px;
}

.form-section {
  margin-bottom: 32px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-section h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-header-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 16px;
}

.section-header-toggle h3 {
  margin: 0;
  border: none;
  padding: 0;
}

.section-header-toggle:hover {
  color: #007bff;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 14px;
}

.required {
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-control[readonly] {
  background-color: #f8f9fa;
  color: #6c757d;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
}

.error-text {
  color: #dc3545;
}

/* 複製 / 產生按鈕組合 */
.input-copy-group,
.input-generate-group {
  display: flex;
  gap: 8px;
}

.input-copy-group .form-control,
.input-generate-group .form-control {
  flex: 1;
}

.input-actions {
  margin-top: 4px;
}

.btn-text {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
}

.btn-text:hover {
  text-decoration: underline;
}

/* checkbox */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: normal;
  cursor: pointer;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* JSON 編輯器 */
.json-textarea {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  resize: vertical;
}

code.code-inline {
  background: #f0f0f0;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
  word-break: break-all;
}

/* 手動輪詢 */
.manual-poll-section {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.poll-result {
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 4px;
}

.poll-result.success {
  background: #d4edda;
  color: #155724;
}

.poll-result.error {
  background: #f8d7da;
  color: #721c24;
}

/* 表單按鈕 */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

/* Badge */
.badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  vertical-align: middle;
}

.badge-secondary {
  background-color: #e2e3e5;
  color: #383d41;
}

/* 右側說明面板 */
.guide-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 20px;
}

.guide-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  overflow: hidden;
  min-width: 0;
}

.guide-card h4 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 16px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.guide-card h4 i {
  color: #007bff;
}

.guide-steps {
  padding-left: 20px;
  margin: 0;
}

.guide-steps li {
  margin-bottom: 14px;
  font-size: 14px;
}

.guide-steps li strong {
  display: block;
  margin-bottom: 4px;
}

.guide-steps li p,
.guide-steps li ul {
  margin: 4px 0 0;
  color: #555;
  font-size: 13px;
}

.guide-steps li ul {
  padding-left: 16px;
}

.guide-steps li ul li {
  margin-bottom: 4px;
}

.guide-note {
  margin-top: 12px;
  padding: 10px 12px;
  background: #e8f4fd;
  border-left: 3px solid #007bff;
  border-radius: 0 4px 4px 0;
  font-size: 13px;
  color: #555;
  display: flex;
  gap: 6px;
}

/* 狀態對照表 */
.status-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.status-table {
  width: 100%;
  min-width: 220px;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.status-table th,
.status-table td {
  padding: 6px 8px;
  border: 1px solid #e0e0e0;
  text-align: left;
  word-break: break-all;
}

.status-table th {
  background: #f8f9fa;
  font-weight: 600;
}

.status-table code {
  background: #f0f0f0;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 12px;
  word-break: break-all;
}

/* 連線方式說明 */
.guide-compare {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.guide-option {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.option-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 6px;
  color: #333;
}

.guide-option p {
  margin: 0;
  font-size: 13px;
  color: #555;
}

/* Spin animation */
.spin {
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-sm {
  padding: 6px 10px;
  font-size: 13px;
}

.btn-primary {
  background-color: #007bff;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #545b62;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #ced4da;
}

.btn-outline:hover {
  background-color: #f8f9fa;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

/* Mapping editor */
.mapping-editor {
  margin-top: 8px;
}

/* WooCommerce 映射成功提示 */
.mapping-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  background: #d4edda;
  border-radius: 6px;
  font-size: 13px;
  color: #155724;
  margin-bottom: 16px;
}

.mapping-notice i {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.mapping-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin: 0 0 8px;
}

/* 視覺化映射表格 */
.mapping-visual-table {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  font-size: 13px;
}

.mvt-header,
.mvt-row {
  display: grid;
  grid-template-columns: 1fr 1.4fr 1.2fr 1fr;
  gap: 0;
}

.mvt-header {
  background: #f8f9fa;
  font-weight: 600;
  font-size: 12px;
  color: #6c757d;
}

.mvt-header > div,
.mvt-row > div {
  padding: 8px 10px;
  border-bottom: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  word-break: break-all;
}

.mvt-header > div:last-child,
.mvt-row > div:last-child {
  border-right: none;
}

.mvt-row:last-child > div {
  border-bottom: none;
}

.mvt-row:nth-child(even) {
  background: #fafafa;
}

.mvt-key code,
.mvt-path code {
  background: #e9ecef;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
}

.mvt-example {
  color: #6c757d;
  font-size: 12px;
}

/* 進階 JSON 展開按鈕 */
.advanced-json-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 13px;
  color: #6c757d;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px dashed #ced4da;
  transition: all 0.2s;
}

.advanced-json-toggle:hover {
  color: #007bff;
  border-color: #007bff;
  background: #f0f7ff;
}
</style>
